import { prenatalScopeFrom } from './prenatalScope';
import React, { useContext } from 'react';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';
import axios from 'src/axios';
import UploadFileList from 'src/components/UI/input/UploadFileList';
import { useAttachmentChanges } from 'src/components/UI/input/attachments';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  SnackContext,
  AppContext,
  ThemeContext,
  UserContext
} from 'src/store/ContextStore';

import { IconButton, Tooltip, InputAdornment, TextField } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getReferralTags, getDependencies } from './formService';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import TextInput from 'src/components/UI/input/TextInput';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import AssociatedNote from 'src/components/UI/input/AssociatedNote';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import { acceptedMessage } from 'src/components/UI/clinicalSuggestions/acceptOutcome';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const StyledCautionContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  position: 'absolute',
  top: 15,
  alignItems: 'center',
  right: 0
});

export default function ClinicalNoteForm({
  data,
  handleClose,
  familyMemberId,
  isProband,
  isEditing,
  staffMemberOptions,
  model,
  isDisabled,
  setUnsavedChanges,
  onSuggestionsAccepted
}) {
  const t = useAutoTranslation();

  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { theme } = useContext(ThemeContext);

  const { user: loggedInUser } = React.useContext(UserContext);

  const patientDob = data?.dob;
  // The age on the calendar day of the note date (calculateAgeOnDate). The date
  // is handed over as stored — a `type: String` column: the picker's instant as
  // its "…Z" string, or a 'YYYY-MM-DD' from the importers and demo seed — and
  // only the raw value says which, so it is read by
  // toLocalDateFromPickerOrImport. `calculateAge` counted elapsed time from a
  // UTC-midnight birth date: a year short on the birthday at any positive
  // offset, and a positive age for a date before the birth, which is now blank.
  // The date field shows the day it read (asPickerDay), so the day on screen is
  // the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (clinicalNoteDate) => calculateAgeOnDate(patientDob, clinicalNoteDate),
    [patientDob]
  );

  const defaultValues = isEditing
    ? { ...model }
    : {
        // PRENATAL SCOPE (decision 21) — see prenatalScope.js. Empty for an ordinary record.
        ...prenatalScopeFrom(model),
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        clinicalNoteDate: new Date(),
        ageAtRecord: computeAgeAtRecord(new Date()),
        clinician:
          staffMemberOptions.find((mem) => mem.value === loggedInUser._id)
            ?.label || '',
        note: '',
        showInFamily: false,
        caution: false,
        pinned: false,
        associatedReferral: null,
        fileAttachments: [],
        reminderEnabled: false,
        reminderDate: null
      };

  const [formValues, setFormValues] = React.useState(defaultValues);
  // Attachment deletes wait for a successful save (TGCLOUD-1850).
  const attachmentChanges = useAttachmentChanges(defaultValues.fileAttachments);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [reminderTouched, setReminderTouched] = React.useState(false);

  let [referralOptions, setReferralOptions] = React.useState(['loading...']);
  const { setSnack } = React.useContext(SnackContext);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // The note's prenatal scope goes with every accept: from a note about a pregnancy no genotype is
  // written onto this person (TGCLOUD-1868, decision 17).
  const clinicalSuggestions = useClinicalSuggestions({
    familyMemberId,
    isProband,
    onAccepted: onSuggestionsAccepted,
    sourceScope: {
      pregnancyId: formValues.pregnancyId,
      fetusId: formValues.fetusId
    }
  });
  const {
    analyzingNote,
    handleAttachmentText,
    analyzeText,
    acceptSuggestions
  } = clinicalSuggestions;

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async (e) => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat,
      t
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: t('Associated record exists; check the {{value}}', {
          value: dep[0]
        }),
        severity: 'error'
      });
      setLoading(false);
      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then((res) => {
        setSnack({
          status: true,
          msg: t('Record deleted'),
          severity: 'success'
        });
        handleCloseModal();
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Delete failed! {{err}}', { err }),
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    const fetchData = async () => {
      const referralTags = await getReferralTags(
        theme,
        familyMemberId,
        formValues.familyId
      );
      setReferralOptions(referralTags);
    };
    if (referralOptions != [] && referralOptions[0] == 'loading...')
      fetchData();
  }, []);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.clinicalNoteDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.clinicalNoteDate]);

  const [error, setError] = React.useState({});

  const handleChange = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));

    if (e.target.name === 'clinicalNoteDate') {
      const nextDate = e.target.value;
      if (!formValues.reminderEnabled || !reminderTouched) {
        setFormValues((prev) => ({ ...prev, reminderDate: nextDate }));
      }
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(nextDate)
      }));
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
  };

  const onToggleSwitch = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    // A family-level note (no member) is always shown in the family. There is
    // no longer a Private switch that forces showInFamily off (TGCLOUD-1766
    // retired the Desktop private flag), so showInFamily follows its own
    // switch — including on a legacy note that still carries `private: true`.
    if (familyMemberId == 'none') {
      setFormValues((prevValues) => ({
        ...prevValues,
        showInFamily: true
      }));
    }
  };

  const validateForm = (values) => {
    let errors = {};

    console.log(values.clinician);

    if (values.clinician === '') {
      errors.clinician = t('Clinician is required.');
    }
    if (values.clinicalNoteDate === '') {
      errors.clinicalNoteDate = t('Date is required.');
    }
    if (values.note === '') {
      errors.note = t('Note is required.');
    }

    if (values.clinicalNoteDate) {
      const selectedDate = new Date(values.clinicalNoteDate);
      const today = new Date();

      if (selectedDate > today) {
        errors.clinicalNoteDate = t('Date cannot be in future');
      }
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    return axios
      .put(APICONSTANTS.clinicalNote, formValues)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          attachmentChanges.commit(formValues.fileAttachments, familyMemberId);
          setSnack({
            status: true,
            msg: t('Successfully saved'),
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: t('Upload failed!'),
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: t('Upload failed! {{err}}', { err }),
          severity: 'error'
        });
      });
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form" style={{ position: 'relative' }}>
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Clinical Note ?"
        content={t(
          'Warning: This action will permanently delete this Clinical Note. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      {formValues.caution && (
        <StyledCautionContainer>
          <ReportProblemIcon sx={{ color: 'red' }} />
          <Typography
            sx={{
              fontWeight: '500',
              color: 'red'
            }}
          >
            {t('Caution Flagged')}
          </Typography>
        </StyledCautionContainer>
      )}
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextInput
            label="Clinician"
            required
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="clinician"
            value={formValues.clinician}
            error={!!error.clinician}
            helperText={error.clinician}
            InputProps={{ readOnly: isDisabled }}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel required>Date</FormLabel>
          </Grid>
          <DateInput
            liveText={true}
            value={asPickerDay(formValues.clinicalNoteDate)}
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'clinicalNoteDate', value: val, type: 'date' }
              })
            }
            variant="outline"
            error={error?.clinicalNoteDate}
            helperText={
              error?.clinicalNoteDate || t('Date cannot be in the future')
            }
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Age at Clinical Note"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND clinical note date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.clinicalNoteDate}
            onChange={
              patientDob && formValues.clinicalNoteDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Associated Referral</FormLabel>
          </Grid>
          <AssociatedNote
            name="associatedReferral"
            handleChange={handleChange}
            value={formValues.associatedReferral}
            noteOptions={referralOptions}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={12}>
          <TextInput
            required
            label="Note"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="note"
            value={formValues.note}
            multiline={true}
            minRows={3}
            error={!!error.note}
            helperText={error.note}
            InputProps={{
              readOnly: isDisabled,
              endAdornment: formValues.note ? (
                <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                  <Tooltip title={t('Analyse for clinical terms')}>
                    <span style={{ lineHeight: 0 }}>
                      <IconButton
                        size="mdium"
                        onClick={() => analyzeText(formValues.note, 'manual')}
                        disabled={analyzingNote || isDisabled}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }}
            borderColor={formValues.caution ? 'red' : ''}
          />
        </Grid>
        <ClinicalSuggestionsPanel
          state={clinicalSuggestions}
          onAccept={() =>
            acceptSuggestions({
              clinicalNoteDate: formValues.clinicalNoteDate,
              clinician: formValues.clinician
            }).then((result) => {
              if (result?.success) {
                setSnack({
                  status: true,
                  msg: acceptedMessage(result, t),
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: t('Failed to save suggestions'),
                  severity: 'error'
                });
              }
            })
          }
          disabled={isDisabled}
        />
        <UploadFileList
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(next) => {
            // Functional: uploads can finish faster than this form re-renders.
            setFormValues((prev) => ({ ...prev, fileAttachments: next }));
            setUnsavedChanges((prev) => ({ ...prev, edit: true }));
          }}
          readOnly={isDisabled}
          onBusyChange={setUploadingFiles}
          onUploadComplete={(uploadData, name) => {
            attachmentChanges.trackUpload(name);
            const text = uploadData?.extractedText;
            if (text) handleAttachmentText(text, name);
          }}
        />
        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>
                {formValues.showInFamily ? t('Yes') : t('No')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Caution
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.caution ?? false}
                onChange={onToggleSwitch}
                name="caution"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>{formValues.caution ? t('Yes') : t('No')}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Pinned
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.pinned ?? false}
                onChange={onToggleSwitch}
                name="pinned"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>{formValues.pinned ? t('Yes') : t('No')}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <Grid>
              <Switch
                checked={!!formValues.reminderEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormValues((prev) => {
                    const next = { ...prev, reminderEnabled: enabled };
                    if (enabled && !prev.reminderDate) {
                      // default reminder date to clinical note date (or today if not set)
                      next.reminderDate = prev.clinicalNoteDate || new Date();
                    }
                    return next;
                  });
                  setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                }}
                name="reminderEnabled"
                color="secondary"
                disabled={isDisabled}
              />
            </Grid>
            {formValues.reminderEnabled && (
              <Grid
                sx={{ marginLeft: 14 }}
                size={{
                  xs: 12,
                  sm: 6
                }}
              >
                <Grid sx={{ paddingBottom: 1 }}>
                  <FormLabel>Reminder Date</FormLabel>
                </Grid>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate}
                  minDate={
                    asPickerDay(formValues.clinicalNoteDate) || new Date()
                  }
                  onChange={(val) => {
                    setReminderTouched(true);
                    handleChange({
                      target: { name: 'reminderDate', value: val, type: 'date' }
                    });
                  }}
                  variant="outline"
                  helperText={t('Defaults to the note date')}
                  readOnly={isDisabled}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing && !isDisabled ? handleOpenModal : null}
          addLabel={`Save new clinical note`}
          saveButtonProps={
            uploadingFiles
              ? {
                  disabled: true,
                  title: 'Waiting for files to finish uploading'
                }
              : undefined
          }
        />
      </Grid>
    </form>
  );
}
